import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/components/Header.css';

const MENU_ITEMS = [
    { name: 'About Us', path: '/about-us' },
    { name: 'Resource Library', path: '/tools' },
    { name: 'Supernova Tools', path: '/supernova-tools' },
    { name: 'Experimentation Zone', path: '/experimentation-zone' },
    { name: 'Fellowships', path: '/fellowships' },
    { name: 'Get Involved', path: '/get-involved' },
    { name: "What's Next", path: '/whats-next' },
    { name: 'Blog', path: '/blog' },
    { name: 'Opportunities', path: '/careers' }
];

const MenuIcon = () => (
    <svg viewBox="0 0 64 48" aria-hidden="true" focusable="false">
        <path d="M19,15 L45,15 C70,15 58,-2 49.0177126,7 L19,37" />
        <path d="M19,24 L45,24 C61.2371586,24 57,49 41,33 L32,24" />
        <path d="M45,33 L19,33 C-8,33 6,-2 22,14 L45,37" />
    </svg>
);

const OrbitField = () => (
    <div className="site-menu-orbits" aria-hidden="true">
        <svg viewBox="0 0 1000 1000" focusable="false">
            <circle className="site-menu-orbit" cx="500" cy="500" r="180" />
            <circle className="site-menu-orbit" cx="500" cy="500" r="300" />
            <circle className="site-menu-orbit" cx="500" cy="500" r="420" />
        </svg>
    </div>
);

const Header = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const desktopToggleRef = useRef(null);
    const mobileToggleRef = useRef(null);
    const menuPanelRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const getVisibleToggle = () => (
            desktopToggleRef.current?.offsetParent !== null
                ? desktopToggleRef.current
                : mobileToggleRef.current
        );

        const handleKeyDown = (event) => {
            const menuPanel = menuPanelRef.current;
            const toggle = getVisibleToggle();

            if (event.key === 'Escape') {
                event.preventDefault();
                setMenuOpen(false);
                window.requestAnimationFrame(() => toggle?.focus());
                return;
            }

            if (event.key !== 'Tab' || !menuPanel || !toggle) {
                return;
            }

            const links = Array.from(menuPanel.querySelectorAll('a[href]'));
            const focusableElements = [toggle, ...links];
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [menuOpen]);

    const toggleMenu = () => {
        setMenuOpen((current) => !current);
    };

    const closeMenu = () => {
        setMenuOpen(false);
    };

    const isActivePath = (path) => (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );

    const menuButton = (ref, className, controlsId) => (
        <button
            ref={ref}
            aria-controls={controlsId}
            aria-expanded={menuOpen}
            className={`menu ${className} ${menuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            type="button"
        >
            <MenuIcon />
        </button>
    );

    return (
        <>
            <header className={`header-main header-desktop ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
                <div className="container-fluid px-4 px-lg-5">
                    <div className="d-flex justify-content-between align-items-center py-4">
                        <Link to="/" className="logo" onClick={closeMenu} aria-label="Supernova Lab home">
                            <img
                                src="/assets/images/logo.svg"
                                alt="Supernova Lab"
                                width={180}
                                height={60}
                            />
                        </Link>

                        {menuButton(desktopToggleRef, 'menu-toggle', 'site-menu-panel')}
                    </div>
                </div>
            </header>

            <header className={`header-main header-mobile ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
                <div className="container-fluid px-3">
                    <div className="d-flex justify-content-between align-items-center py-2">
                        <Link to="/" className="logo-mobile" onClick={closeMenu} aria-label="Supernova Lab home">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 65 75" fill="none" aria-hidden="true">
                                <path d="M5.02684 38.8821C7.45261 31.3409 26.8461 29.6064 44.2538 34.7847C45.1713 35.0612 46.0637 35.3503 46.931 35.6519C43.5123 33.9426 39.6662 32.3966 35.5186 31.1523C18.4 26.0117 2.61364 27.7336 0.288429 34.9984C-0.930737 38.8067 1.78411 43.3943 7.12582 47.6299C4.97657 44.5632 4.19731 41.4964 5.02684 38.8821Z" fill="currentColor" />
                                <path d="M57.074 43.4826C57.074 43.4826 57.0363 43.4826 57.0237 43.4701C59.9899 46.5117 61.3599 49.5408 60.5304 52.0922C60.1408 53.2737 59.2987 54.2415 58.0795 55.0207C58.0166 55.0584 57.9664 55.0961 57.9035 55.1339C57.7778 55.2093 57.6647 55.2847 57.5265 55.3475C56.7472 55.7749 55.8297 56.1519 54.8116 56.4536C54.3717 56.5918 53.9318 56.7426 53.4919 56.8809C45.2217 59.1056 34.4503 57.7104 25.2374 55.1967C24.5713 55.0584 23.9177 54.9076 23.2516 54.7442C21.1023 54.2415 19.041 53.6507 17.0803 52.9972C17.1055 53.0349 17.1306 53.0726 17.1683 53.0852C19.3553 54.3169 21.6428 55.4229 23.9931 56.4033C31.107 59.3695 38.6859 61.3931 46.4911 61.7701C50.5131 61.921 54.8745 61.9712 58.7456 60.3122C67.9962 56.1142 64.9797 47.0396 57.0866 43.4701L57.074 43.4826Z" fill="currentColor" />
                                <path d="M24.6843 68.758C15.0064 65.1759 10.6073 53.0094 12.5555 38.1658C8.16899 55.8374 11.9773 71.5986 21.8438 74.5271C28.2412 76.425 35.7448 72.4909 42.1171 64.8743C36.1093 69.022 29.7872 70.6559 24.6843 68.758Z" fill="currentColor" />
                                <path d="M44.8444 0.484767C35.1665 -2.45631 22.9748 8.26481 16.0117 25.0566C23.1256 13.0158 32.9921 6.06528 41.1492 8.55389C52.0462 11.872 55.7289 30.788 49.3691 50.7974C49.168 51.4384 48.9543 52.0668 48.7407 52.6952C50.2866 49.5908 51.6692 46.2349 52.8004 42.6528C59.1476 22.6309 55.5906 3.75263 44.857 0.484767H44.8444Z" fill="currentColor" />
                            </svg>
                        </Link>

                        {menuButton(mobileToggleRef, 'menu-toggle-mobile', 'site-menu-panel')}
                    </div>
                </div>
            </header>

            <div
                ref={menuPanelRef}
                className={`site-menu-overlay ${menuOpen ? 'is-open' : ''}`}
                id="site-menu-panel"
                aria-hidden={!menuOpen}
            >
                <div className="site-menu-gradient" aria-hidden="true" />
                <div className="site-menu-gradient-blur" aria-hidden="true" />
                <OrbitField />

                <nav className="site-menu-links" aria-label="Primary navigation">
                    {MENU_ITEMS.map((item, index) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`site-menu-link ${isActivePath(item.path) ? 'is-current' : ''}`}
                            style={{ '--link-order': index }}
                            onClick={closeMenu}
                            tabIndex={menuOpen ? 0 : -1}
                            aria-current={isActivePath(item.path) ? 'page' : undefined}
                        >
                            <span className="site-menu-anchor" aria-hidden="true" />
                            <span className="site-menu-index">{String(index + 1).padStart(2, '0')}</span>
                            <span className="site-menu-label">{item.name}</span>
                        </Link>
                    ))}
                </nav>
            </div>
        </>
    );
};

export default Header;
